<template>
  <div>
    <h1>
      Incident Details
      <small class="align-middle">
        <span class="badge" :class="badge">{{ incident.status }}</span>
      </small>
    </h1>
    <div class="btn-group float-right" role="group" aria-label="Change status">
      <button class="btn btn-default" @click="changeStatus" :disabled="changingStatus">
        <span class="fas" :class="statusChangeLabelIcon" v-if="!changingStatus"></span>
        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true" v-if="changingStatus"></span>
        {{ statusChangeLabel }}
      </button>
      <button class="btn btn-default" @click="jumpToCommentForm"><span class="fas fa-comment"></span> Comment</button>
      <slot name="additionalActions"></slot>
    </div>
    <ul class="list-unstyled">
      <li>Participant: <strong>{{ incident.participant.name }}</strong></li>
      <li v-if="staffReported">Reported by: <strong>{{ incident.reporter.name }}</strong></li>
      <li>{{ triggerAttachments.length > 1 ? 'First triggered' : 'Triggered' }} on: <strong>{{ formatDate(firstTriggeredDate) }}</strong></li>
      <li v-if="triggerAttachments.length > 1">Latest trigger: <strong>{{ formatDate(latestTriggeredDate) }}</strong></li>
      <li v-if="triggerAttachments.length > 1">Number of occurrences: <strong>{{ triggerAttachments.length }}</strong></li>
      <li>Type: <strong>{{ incident.type }}</strong></li>
      <li>Status: <strong>{{ incident.status }}</strong></li>
      <li v-if="allTags.length > 0" class="form-inline">
        Tags:
        <Tags :allTags="allTags" :activeTags="incident.tags" :incidentId="incident.id" @update-tags="updateTags"></Tags>
      </li>
    </ul>
    <div class="clearfix mb-2">
      <small>Show: </small>
      <ul class="filter-buttons list-inline d-inline">
        <li v-if="triggerAttachments.length > 1" class="list-inline-item">
          <button
            class="btn btn-sm btn-default"
            :class="{ active: showRecurrences }"
            @click="showRecurrences = !showRecurrences"
          >
            <span class="far" :class="{'fa-check-square': showRecurrences, 'fa-square': !showRecurrences}"></span>
            Recurrences
          </button>
        </li>
        <li v-for="filter in filters" class="list-inline-item">
          <button
            class="btn btn-sm btn-default"
            :class="{ active: filter.selected }"
            @click="filter.selected = !filter.selected"
          >
            <span class="far" :class="{'fa-check-square': filter.selected, 'fa-square': !filter.selected}"></span>
            {{ filter.label }}
          </button>
        </li>
      </ul>

      <button class="btn btn-sm btn-default float-right" @click="changeSort">{{sortDescription}} <span class="fas" :class="sortIcon"></span></button>
    </div>

    <ul class="list-unstyled well">
      <li v-if="sortDesc">
        <b-overlay :show="changingStatus">
          <CommentForm ref="commentFormTop" :currentUser="currentUser" :incident="incident" :collapsible="collapsibleCommentForm" @comment-success="updateIncident"></CommentForm>
        </b-overlay>
        <hr>
      </li>
      <li v-for="(attachment, idx) in filteredAttachments" :key="idx">
        <div class="mb-2">
          <span class="font-italic ">{{ formatDate(attachment.created_at) }}</span>
          <template v-if="showAttachmentUser(attachment)">
            - <span class="fas fa-user"></span> {{ attachment.user.name }}
          </template>
        </div>
        <component
          :is="attachment.link_type"
          :attachment="attachment"
        />
        <hr v-if="!sortDesc || idx < filteredAttachments.length - 1">
      </li>
      <li v-if="!sortDesc">
        <b-overlay :show="changingStatus">
          <CommentForm ref="commentFormBottom" :currentUser="currentUser" :incident="incident" :collapsible="collapsibleCommentForm" @comment-success="updateIncident"></CommentForm>
        </b-overlay>
      </li>
    </ul>
  </div>
</template>

<script>
  import dayjs from 'dayjs';
  import { BOverlay } from 'bootstrap-vue';

  import Tags from './components/UI/Tags.vue'
  import CommentForm from './components/UI/CommentForm.vue'
  import Comment from './components/Comment.vue'
  import Manual from './components/Manual.vue'
  import Related from './components/Related.vue'
  import StatusChange from './components/StatusChange.vue'
  import Trigger from './components/Trigger.vue'

  const typeRankEnum = {
    "Trigger": 0,
    "Related": 1,
    "Manual": 2,
    "Comment": 3,
    "StatusChange": 4,
  }

  export default {
    components: {
      Comment,
      Manual,
      Related,
      StatusChange,
      Trigger,
      CommentForm,
      Tags,
      BOverlay,
    },
    props: {
      initialIncident: {
        type: Object,
        default: function () {
          return {};
        }
      },
      currentUser: {
        type: Object,
        default: function () {
          return {};
        }
      },
      allTags: {
        type: Array,
        default: function () {
          return [];
        }
      },
      collapsibleCommentForm: {
        type: Boolean,
        default: true
      }
    },
    provide() {
      return {
        participantId: this.incident.participant_id
      }
    },
    data() {
      return {
        incident: this.initialIncident,
        sortDesc: true,
        filters: [
          { label: 'Comments', types: ['Comment'], selected: true },
          { label: 'Related Attachments', types: ['Related','Manual'], selected: true },
          { label: 'Status Changes', types: ['StatusChange'], selected: false },
        ],
        showRecurrences: true,
        changingStatus: false,
      }
    },
    created() {
      if (window.localStorage) {
        this.sortDesc = JSON.parse(window.localStorage.getItem('w2h_incident_sort')) ?? true;
      }
    },
    computed: {
      filteredAttachments() {
        let attachments = this.incident.attachments
          .filter(this.attachmentFilterCallback)
          .sort((a, b) => {
            if (a.created_at === b.created_at) {
              const aType = typeRankEnum[a.link_type] ?? 10;
              const bType = typeRankEnum[b.link_type] ?? 10;
              return aType < bType ? -1 : 1;
            } else {
              return a.created_at < b.created_at ? -1 : 1;
            }
          });
        // This should be the first item in the array, but let's find it just to be safe
        const firstTrig = attachments.find(attachment => attachment.link_type === 'Trigger');
        // add notifications and inbasket messages to the first trigger for display, in the future this will be
        // linked properly and pulled in correctly
        firstTrig.notifications_sent = {
          notifications: this.incident.notifications,
          inbasketMessages: this.inbasketMessagesForNotifications
        };

        if (this.sortDesc) {
          attachments = attachments.reverse();
        }
        return attachments;
      },
      staffReported() {
        return this.incident.reporter.Participants.length === 0;
      },
      triggerAttachments() {
        return this.incident.attachments
          .filter((attachment) => attachment.link_type === 'Trigger')
          .sort((a, b) => a.created_at < b.created_at ? -1 : 1);
      },
      firstTriggeredDate() {
        return this.triggerAttachments[0].created_at;
      },
      latestTriggeredDate() {
        return this.triggerAttachments[this.triggerAttachments.length - 1].created_at;
      },
      inbasketMessagesForNotifications() {
        // don't return attached inbasket messages if this is an incident generated due to failure to send an inbasket message
        if (this.triggerAttachments[0].comment?.startsWith('Sending Epic Inbasket Message failed')) {
          return [];
        }
        return this.incident.attachments
          .filter(attachment => attachment.object_type === "EpicInbasketMessage")
          .map(attachment => attachment.details);
      },
      badge() {
        switch (this.incident.status) {
          case 'Open':
            return 'badge-info';
          case 'Resolved':
            return 'badge-success'
          case 'New':
          default:
            return 'badge-danger';
        }
      },
      sortDescription() {
        return this.sortDesc ? 'Newest first' : 'Oldest first';
      },
      sortIcon() {
        return this.sortDesc ? 'fa-arrow-up' : 'fa-arrow-down';
      },
      filterTypes() {
        return this.filters.filter(f => f.selected).map(f => f.types).flat().concat(['Trigger']);
      },
      statusChangeLabel() {
        return this.incident.status === 'Resolved' ? 'Reopen' : 'Resolve';
      },
      statusChangeLabelIcon() {
        return this.incident.status === 'Resolved' ? 'fa-redo-alt' : 'fa-check';
      }
    },
    methods: {
      formatDate(date) {
        return dayjs(date).format('MM/DD/YY hh:mm A');
      },
      showAttachmentUser(attachment) {
        // only show attachment user if the user does not have any participant records
        return attachment.user_id && attachment.user.Participants.length === 0;
      },
      updateIncident(incident) {
        // Note: if we ever add pagination or secondary loads, we will need to re-evaluate this strategy, but it works for now
        this.incident = incident;
      },
      updateTags(tags) {
        this.incident.tags = tags;
      },
      changeSort() {
        this.sortDesc = !this.sortDesc;
        if (window.localStorage) {
          window.localStorage.setItem('w2h_incident_sort', this.sortDesc);
        }
      },
      jumpToCommentForm() {
        const form = this.sortDesc ? this.$refs.commentFormTop : this.$refs.commentFormBottom;
        form.showCommentForm();
        form.$el.scrollIntoView({behavior: "smooth"});
      },
      async changeStatus(e) {
        const url = `${this.$config.baseUrl}/api/v2/incidents/${this.incident.id}?include=${this.$config.apiIncludes}`;

        const value = this.incident.status === 'Resolved' ? 'Open' : 'Resolved';
        let body = [{
          "path": "/status",
          "op": "replace",
          "value": value,
        }];

        this.changingStatus = true;
        const res = await fetch(url, {
          method: "PATCH",
          body: JSON.stringify(body)
        });
        this.changingStatus = false;
        if (!res.ok) {
          alert('Something went wrong, please refresh the page and try again');
          return;
        }
        const data = (await res.json()).data;
        this.updateIncident(data);
      },
      attachmentFilterCallback(attachment) {
        // Filter out Epic Inbasket messages, so we can show notifications on the trigger instead
        // unless this is an incident for an Epic Inbasket message that failed to send
        if (attachment.object_type === 'EpicInbasketMessage' && !this.triggerAttachments[0].comment?.startsWith('Sending Epic Inbasket Message failed')) {
          return false;
        }

        // Filter out any unselected attachment types
        if (!this.filterTypes.includes(attachment.link_type)) {
          return false;
        }

        // If we have multiple trigger attachments but the 'Recurrences' filter is not enabled, filter out
        // all trigger attachments besides the original
        if (!this.showRecurrences) {
          if (attachment.link_type === 'Trigger' && attachment.created_at != this.firstTriggeredDate) {
            return false;
          }
        }

        return true;
      }
    },
  }
</script>

<style scoped>
  /*
   * Don't let unselected filters look like active filters while focused
   */
  .filter-buttons .btn.btn-default:not(.active):active, .filter-buttons .btn.btn-default:not(.active):focus {
    box-shadow: none;
    color: #212529;
    background-color: #fff;
    border-color: #dee2e6;
  }

</style>